import { $$ } from './dom.js';

/**
 * Dropdown menus driven by clicks rather than by focus.
 *
 * daisyUI reveals `.dropdown-content` through `:focus` and `:focus-within`.
 * That is enough on a desktop, where clicking a `<button>` focuses it, and it
 * is nothing at all on iOS: tapping a button there dispatches `click` without
 * granting focus, so `:focus-within` never matches and the menu never opens.
 *
 * The language switcher was therefore completely dead on a phone — no error, no
 * animation, nothing to report beyond "the button does nothing". The account
 * menu had the same defect, unreported only because fewer people look for it.
 *
 * `.dropdown-open` is the class daisyUI already honours for exactly this, so
 * the fix drives that one class from the `click` event every browser sends.
 * Focus keeps working on its own for keyboard users; `Escape` and an outside
 * click close, which is what a pointer user expects and what focus alone cannot
 * offer on a touchscreen.
 */

const OPEN = 'dropdown-open';

let wired = false;

export function initDropdowns(root: ParentNode = document): void {
  for (const container of $$<HTMLElement>('[data-dropdown]', root)) {
    const trigger = container.querySelector<HTMLElement>('[data-dropdown-trigger]');
    if (!trigger || trigger.dataset.dropdownWired) continue;

    trigger.dataset.dropdownWired = 'true';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'true');

    trigger.addEventListener('click', (event) => {
      // Without this the click reaches the document, and the global close
      // handler would undo the opening within the same gesture.
      event.stopPropagation();
      toggle(container, !container.classList.contains(OPEN));
    });
  }

  // The global listeners are attached once only: `initDropdowns` runs on every
  // page mount, and stacking them would close the menu as many times as there
  // have been mounts.
  if (wired) return;
  wired = true;

  document.addEventListener('click', (event) => {
    const target = event.target as Node | null;

    for (const container of $$<HTMLElement>('[data-dropdown].' + OPEN)) {
      // A click inside the menu follows a link: closing before the navigation
      // starts would make it blink for nothing.
      if (target && container.contains(target)) continue;
      toggle(container, false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    for (const container of $$<HTMLElement>('[data-dropdown].' + OPEN)) {
      toggle(container, false);
    }
  });
}

function toggle(container: HTMLElement, open: boolean): void {
  if (open) {
    // One menu open at a time: two stacked panels overlap, and the second one
    // looks empty.
    for (const other of $$<HTMLElement>('[data-dropdown].' + OPEN)) {
      if (other !== container) toggle(other, false);
    }
  }

  container.classList.toggle(OPEN, open);

  const trigger = container.querySelector<HTMLElement>('[data-dropdown-trigger]');
  trigger?.setAttribute('aria-expanded', String(open));

  /*
   * Focus is dropped on close, otherwise daisyUI's `:focus-within` rule would
   * hold the panel open after the class has just been removed — the menu would
   * only shut on the next click, somewhere else.
   */
  if (!open) trigger?.blur();
}
